import {IconButton} from '@dropbox/dig-components/buttons';
import {Chip} from '@dropbox/dig-components/chip';
import {Menu} from '@dropbox/dig-components/menu';
import {Truncate} from '@dropbox/dig-components/truncate';
import {atoms, Box, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {AddLine, DeleteLine, EditLine} from '@dropbox/dig-icons/assets';
import {LinkIcon} from 'components/shared/LinkIcon';
import {t} from 'i18next';
import type {MouseEvent} from 'react';

import {WorkstreamModal} from './WorkstreamModal';
import {WorkstreamModalField, WorkstreamModalFieldError} from './WorkstreamModalField';

export type ResourcesMenuItem = {
  id?: number | null;
  url: string;
  text?: string | null;
  icon?: string | null;
  isEditable: boolean;
};

/**
 * Compact resources control for the properties-row sidebar layout, modeled on
 * the Ask composer's "N resources" chip: a dropdown of links (open in a new
 * tab; the pencil edits and the trash can removes editable ones) ending in
 * "Add resource". With no resources it's a plain "+ Add resource" chip.
 */
export const SidebarResourcesMenu = ({
  resources,
  canEdit,
  disabled,
  onAdd,
  onOpen,
  onEdit,
  onRemove,
}: {
  resources: ResourcesMenuItem[];
  canEdit: boolean;
  disabled?: boolean;
  onAdd: () => void;
  onOpen: (resource: ResourcesMenuItem) => void;
  onEdit: (resource: ResourcesMenuItem) => void;
  onRemove: (resource: ResourcesMenuItem) => void;
}) => {
  if (resources.length === 0) {
    return canEdit ? (
      <Chip size="small" variant="standard" onClick={onAdd} disabled={disabled}>
        <Chip.IconAccessory>
          <UIIcon src={AddLine} />
        </Chip.IconAccessory>
        <Chip.Content>{t('workstream_add_resource')}</Chip.Content>
      </Chip>
    ) : null;
  }

  const label = t('workstream_resources', {count: resources.length});
  return (
    <Menu.Wrapper shouldPropagateClickOutsideMouseEvents closeOnSelection={false}>
      {({getContentProps, getTriggerProps}) => (
        <>
          <Chip
            {...getTriggerProps()}
            size="small"
            variant="standard"
            withDropdownIcon
            aria-label={label.toString()}
          >
            <Chip.Content>{label}</Chip.Content>
          </Chip>
          <Menu.Content {...getContentProps()} placement="bottom-start" minWidth="280px">
            <Menu.Segment>
              {resources.map((resource) => {
                const title = resource.text || resource.url;
                return (
                  <Menu.ActionItem
                    key={`${resource.id ?? 'resource'}-${resource.url}`}
                    onClick={() => {
                      onOpen(resource);
                      window.open(resource.url, '_blank', 'noopener,noreferrer');
                    }}
                    withLeftAccessory={
                      <LinkIcon
                        icon={resource.icon ?? undefined}
                        size="small"
                        className={atoms({color: 'Text Subtle'})}
                      />
                    }
                    withRightAccessory={
                      canEdit && resource.isEditable ? (
                        <Box display="flex" alignItems="center">
                          <IconButton
                            variant="transparent"
                            size="small"
                            disabled={disabled}
                            aria-label={`${t('edit')} ${title}`}
                            onClick={(event: MouseEvent<HTMLButtonElement>) => {
                              event.stopPropagation();
                              event.preventDefault();
                              onEdit(resource);
                            }}
                          >
                            <UIIcon
                              src={EditLine}
                              size="small"
                              className={atoms({color: 'Text Subtle'})}
                            />
                          </IconButton>
                          <IconButton
                            variant="transparent"
                            size="small"
                            disabled={disabled}
                            aria-label={`${t('remove')} ${title}`}
                            onClick={(event: MouseEvent<HTMLButtonElement>) => {
                              event.stopPropagation();
                              event.preventDefault();
                              onRemove(resource);
                            }}
                          >
                            <UIIcon
                              src={DeleteLine}
                              size="small"
                              className={atoms({color: 'Text Subtle'})}
                            />
                          </IconButton>
                        </Box>
                      ) : undefined
                    }
                  >
                    <Menu.RowTitle>
                      <Truncate maxWidth={260}>{title}</Truncate>
                    </Menu.RowTitle>
                    {resource.text ? (
                      <Menu.RowSubtitle>
                        <Truncate maxWidth={260}>{resource.url}</Truncate>
                      </Menu.RowSubtitle>
                    ) : null}
                  </Menu.ActionItem>
                );
              })}
            </Menu.Segment>
            {canEdit && (
              <Menu.Segment>
                <Menu.ActionItem
                  disabled={disabled}
                  onClick={onAdd}
                  withLeftAccessory={<UIIcon src={AddLine} size="small" />}
                >
                  {t('workstream_add_resource')}
                </Menu.ActionItem>
              </Menu.Segment>
            )}
          </Menu.Content>
        </>
      )}
    </Menu.Wrapper>
  );
};

/** URL + name modal for adding or editing a workstream resource (state lives in the sidebar). */
export const AddResourceModal = ({
  open,
  isEditing,
  url,
  name,
  urlInvalid,
  error,
  canSubmit,
  saving,
  onUrlChange,
  onNameChange,
  onCancel,
  onSubmit,
}: {
  open: boolean;
  isEditing: boolean;
  url: string;
  name: string;
  urlInvalid: boolean;
  error?: string;
  canSubmit: boolean;
  saving: boolean;
  onUrlChange: (value: string) => void;
  onNameChange: (value: string) => void;
  onCancel: () => void;
  onSubmit: () => void;
}) => (
  <WorkstreamModal
    open={open}
    title={isEditing ? t('workstream_edit_resource') : t('workstream_add_resource')}
    onClose={onCancel}
    actionLabel={isEditing ? t('save') : t('add')}
    actionProps={{
      type: 'submit',
      form: 'add-resource-form',
      disabled: !canSubmit || saving,
    }}
  >
    <form
      id="add-resource-form"
      onSubmit={(event) => {
        event.preventDefault();
        if (canSubmit && !saving) onSubmit();
      }}
    >
      <Stack gap="16">
        <WorkstreamModalField
          label={t('url')}
          value={url}
          onChange={(event) => onUrlChange(event.target.value)}
          placeholder="https://"
          autoFocus
          isInvalid={urlInvalid}
        />
        <WorkstreamModalField
          label={t('name')}
          value={name}
          onChange={(event) => onNameChange(event.target.value)}
        />
        {error && <WorkstreamModalFieldError>{error}</WorkstreamModalFieldError>}
      </Stack>
    </form>
  </WorkstreamModal>
);
